package com.dynamicruntime.common.util

import com.dynamicruntime.common.annotation.KdrPrivate
import com.dynamicruntime.common.exception.KdrException
import kotlinx.datetime.LocalDate
import kotlin.math.round
import kotlin.time.Instant

// Extension methods for converting / coercing values.

/**
 * Forcibly coerces this value to the parameterized type [T] via an unchecked cast, **refusing null**: a null
 * here is a broken invariant (a key the caller knows was put there, a parse that cannot have failed), and it
 * fails as a conversion error at the site rather than as a null further on. The caller is responsible for the
 * value actually being a [T] -- this centralizes the (suppressed) unchecked cast in one place. The
 * "required" half of a pair, like `getReqStr` beside `getOptStr`: a value that may legitimately be absent
 * goes through [toOptT].
 */
@Suppress("UNCHECKED_CAST")
fun <T> Any?.toT(): T = (this ?: throw KdrException.mkConv("Expected a value to coerce but found null.")) as T

/** The nullable counterpart of [toT]: null stays null, anything else is coerced to [T] the same way. */
@Suppress("UNCHECKED_CAST")
fun <T> Any?.toOptT(): T? = this as T?

/** Forcibly coerces this value to a JSON-style `Map<String, Any?>` (see [toT]). */
fun Any.toJsonMap(): Map<String, Any?> = toT()

/**
 * A null-tolerant view of a (loosely typed) parsed-JSON value as a `Map<String, Any?>`, or empty when it is
 * null or not a map. Unlike [toJsonMap] (which assumes a map), this guards first -- handy when reaching into a
 * decoded JSON tree, e.g., an endpoint response envelope or a UI-config payload.
 */
fun Any?.toJsonMapOrEmpty(): Map<String, Any?> = if (this is Map<*, *>) toJsonMap() else emptyMap()

/** A null-tolerant view of a parsed-JSON value as a `List<Any?>`, or empty when it is null or not a list. */
fun Any?.toJsonListOrEmpty(): List<Any?> = this as? List<*> ?: emptyList()

/**
 * A parsed-JSON value viewed as a `List<Map<String, Any?>>` -- each element coerced with [toJsonMapOrEmpty]
 * (a non-map element becomes an empty map); empty when the value is null or not a list. The common shape for a
 * JSON array of objects (a UI-config's links, the endpoint catalog, a list-endpoint payload). Ported from dn's
 * `toOptListOfMaps`, renamed to sit beside [toJsonMap] / [toJsonListOrEmpty].
 */
fun Any?.toJsonListOfMaps(): List<Map<String, Any?>> = toJsonListOrEmpty().map { it.toJsonMapOrEmpty() }

/**
 * A parsed-JSON value viewed as a `List<String>` -- each element rendered via `toString()`, nulls dropped;
 * empty when the value is null or not a list. The common shape for a JSON array of strings (roles, tags,
 * choice values). Ported from dn's `toOptListOfStrings`.
 */
fun Any?.toJsonListOfStrings(): List<String> = (this as? List<*>)?.mapNotNull { it?.toString() } ?: emptyList()

/**
 * This value rendered as text when it is one -- a [CharSequence] -- or when it is another primitive: a
 * [Number], a [Boolean] or a [Char]. Anything else, null included, yields null.
 *
 * It converts, like every other `toOptX` here, rather than filtering. Until issue #267 it admitted only a
 * [CharSequence], and the failure mode of that is worth remembering because it is silent and, worse,
 * *symmetric*: a conversion answering null reads as "absent", and two absents compare equal. So `42` and
 * `true` did not merely fail to convert, they converted to the same thing -- which made a schema `const` of a
 * non-string match anything at all, and a condition watching a boolean always take its `then` branch.
 *
 * The line sits at primitives deliberately. `toString()` on a [Map] yields Kotlin's `{a=1}`, which is not JSON
 * and would trade a silent null for a plausible-looking wrong answer. Naming an arbitrary value -- in a
 * message, say -- is a different job, and [fmt] does it honestly for objects too.
 *
 * Rendered *through* [fmt] rather than `toString`, because a bare `toString` on a [Double] disagrees across
 * platforms: `1.0` is `"1.0"` on the JVM and `"1"` under Kotlin/JS (both measured). This is kernel code that
 * the browser and the server both run, so a value has to spell itself the same way on each.
 */
fun Any?.toOptStr(): String? = when (this) {
    is CharSequence, is Number, is Boolean, is Char -> fmt()
    else -> null
}

/**
 * Coerces this value to a member of enum [T] by matching its [Enum.name], or null when it is null, not a
 * string, or not a member name. `inline`/`reified` because a generic enum lookup needs the concrete type at the
 * call site. Pairs with the schema's `options(...)` choice list (both keyed off the same enum), so an endpoint
 * can branch on `req[*key*].toOptEnum<MyEnum>()` exhaustively while validation already rejected any other value.
 */
inline fun <reified T : Enum<T>> Any?.toOptEnum(): T? =
    toOptStr()?.let { s -> enumValues<T>().firstOrNull { it.name == s } }

/**
 * Loosely coerces this string to a boolean, tolerating the many spellings found in CSV and other loose
 * data sources (yes/no, y/n, t/f, true/false, 1/0). Examines the first non-whitespace character
 * (case-insensitively): 'y'/'t'/'1' -> true, 'n'/'f'/'0' -> false, anything else -> null. A string with
 * no non-whitespace character also yields null.
 *
 * Mishandled booleans are one of the most common data errors, so this errs toward simplicity: it will
 * over-accept (e.g. "tremendous" reads as true) rather than risk silently mangling a real boolean.
 *
 * Whitespace is treated as any character whose code point is <= a space; this deliberately does not
 * forgive exotic locale-specific whitespace above a space.
 */
fun String.toOptBool(): Boolean? {
    for (ch in this) {
        if (ch > ' ') {
            return when (ch.lowercaseChar()) {
                'y', 't', '1' -> true
                'n', 'f', '0' -> false
                else -> null
            }
        }
    }
    return null
}

/**
 * Loosely coerces this value to a [Long]: null (and a blank string) yield null; a [Number] is narrowed; a
 * numeric string is parsed (tolerating a fractional part, which is truncated). A non-numeric string or an
 * otherwise unconvertible value throws [KdrException.mkConv] -- unlike [toOptStr]/[toOptBool], a malformed
 * number is surfaced rather than silently dropped, since it usually signals bad stored data or a bad bind.
 * [toOptLongOrNull] is the form that reads a malformed value as null too.
 */
fun Any?.toOptLong(): Long? = toOptLongResult().orThrow()

/** [toOptLong], but a malformed or unconvertible value is null as well as an absent one (issue #909). */
fun Any?.toOptLongOrNull(): Long? = toOptLongResult().valueOrNull()

/** [toOptLong]'s outcome as a value (issue #909): the number (null when absent), or the problem with it. */
fun Any?.toOptLongResult(): Parsed<Long?> = when (this) {
    null -> Parsed.Ok(null)
    is Long -> Parsed.Ok(this)
    is Number -> Parsed.Ok(this.toLong())
    is CharSequence -> {
        val s = this.trim().toString()
        if (s.isEmpty()) {
            Parsed.Ok(null)
        } else {
            (s.toLongOrNull() ?: s.toDoubleOrNull()?.toLong())?.let { Parsed.Ok(it) }
                ?: Parsed.failed(ConvProblem.badFormat, "Cannot convert '$s' to an integer.")
        }
    }
    else -> Parsed.failed(
        ConvProblem.wrongType, "Cannot convert value of type ${this::class.simpleName} to an integer.",
    )
}

/** Loosely coerces this value to a [Double]; see [toOptLong] for the null/parse/throw semantics. */
fun Any?.toOptDouble(): Double? = toOptDoubleResult().orThrow()

/** [toOptDouble], but a malformed or unconvertible value is null as well as an absent one (issue #909). */
fun Any?.toOptDoubleOrNull(): Double? = toOptDoubleResult().valueOrNull()

/** [toOptDouble]'s outcome as a value (issue #909): the number (null when absent), or the problem with it. */
fun Any?.toOptDoubleResult(): Parsed<Double?> = when (this) {
    null -> Parsed.Ok(null)
    is Double -> Parsed.Ok(this)
    is Number -> Parsed.Ok(this.toDouble())
    is CharSequence -> {
        val s = this.trim().toString()
        if (s.isEmpty()) {
            Parsed.Ok(null)
        } else {
            s.toDoubleOrNull()?.let { Parsed.Ok(it) }
                ?: Parsed.failed(ConvProblem.badFormat, "Cannot convert '$s' to a number.")
        }
    }
    else -> Parsed.failed(
        ConvProblem.wrongType, "Cannot convert value of type ${this::class.simpleName} to a number.",
    )
}

/**
 * Loosely coerces this value to an [Instant]: null (and a blank string) yield null; an [Instant] passes
 * through; an epoch-millis [Number] is converted at millisecond precision; a string is parsed via
 * [parseDate]. Anything else throws [KdrException.mkConv]; [toOptInstantOrNull] reads it as null instead.
 *
 * Deliberately KMP-safe: it knows nothing about JVM/JDBC date types. The only place a `java.util.Date` /
 * `java.sql.Timestamp` enters the runtime is the JDBC boundary, so the SQL layer's `toDbInstant` layers
 * those on top of this shared coercer rather than burdening it (and every transpile target) with them.
 */
fun Any?.toOptInstant(): Instant? = toOptInstantResult().orThrow()

/** [toOptInstant], but a malformed or unconvertible value is null as well as an absent one (issue #909). */
fun Any?.toOptInstantOrNull(): Instant? = toOptInstantResult().valueOrNull()

/** [toOptInstant]'s outcome as a value (issue #909): the instant (null when absent), or the problem with it. */
fun Any?.toOptInstantResult(): Parsed<Instant?> = when (this) {
    null -> Parsed.Ok(null)
    is Instant -> Parsed.Ok(this)
    // A day has no instant of its own; this is the caller explicitly asking for one (issue #189).
    is LocalDate -> Parsed.Ok(this.toStartOfDay())
    is Number -> Parsed.Ok(Instant.fromEpochMilliseconds(this.toLong()))
    is CharSequence -> {
        val s = this.trim().toString()
        if (s.isEmpty()) Parsed.Ok(null) else s.parseDateResult()
    }
    else -> Parsed.failed(
        ConvProblem.wrongType, "Cannot convert value of type ${this::class.simpleName} to a date.",
    )
}

/**
 * Loosely coerces this value to a [LocalDate] — the day-only counterpart of [toOptInstant]: null (and a blank
 * string) yield null; a [LocalDate] passes through; an [Instant] is narrowed to its day in the server zone; a
 * string is parsed via [parseDay]. Anything else throws [KdrException.mkConv]; [toOptLocalDateOrNull] reads it
 * as null instead.
 *
 * This is the accessor for a `format: "date"` field, whose coerced value is a [LocalDate] rather than an
 * instant precisely so the day cannot drift.
 */
fun Any?.toOptLocalDate(): LocalDate? = toOptLocalDateResult().orThrow()

/** [toOptLocalDate], but a malformed or unconvertible value is null as well as an absent one (issue #909). */
fun Any?.toOptLocalDateOrNull(): LocalDate? = toOptLocalDateResult().valueOrNull()

/** [toOptLocalDate]'s outcome as a value (issue #909): the day (null when absent), or the problem with it. */
fun Any?.toOptLocalDateResult(): Parsed<LocalDate?> = when (this) {
    null -> Parsed.Ok(null)
    is LocalDate -> Parsed.Ok(this)
    is Instant -> Parsed.Ok(this.toDay())
    is CharSequence -> {
        val s = this.trim().toString()
        if (s.isEmpty()) Parsed.Ok(null) else s.parseDayResult()
    }
    else -> Parsed.failed(
        ConvProblem.wrongType, "Cannot convert value of type ${this::class.simpleName} to a day.",
    )
}


// --- map-field accessors (a required/optional value at a key) ---------------------------------------------

/** The value at [key] rendered as a String, or a bad-input error if it is absent/null. */
fun Map<String, Any?>.getReqStr(key: String): String =
    this[key]?.toString() ?: throw KdrException.mkInput("Missing required field '$key'.")

/** The value at [key] rendered as a String, or null if it is absent/null. */
fun Map<String, Any?>.getOptStr(key: String): String? = this[key]?.toString()

/**
 * The value at [key] as a **trimmed** String, or null when it is absent, null, or **blank** -- a whitespace-only
 * value counts as blank and reads as absent. Unlike [getOptStr], which passes a blank value straight through.
 */
fun Map<String, Any?>.getOptNonBlankStr(key: String): String? = getOptStr(key).trimToNull()

/**
 * The value at [key] as a trimmed, non-blank String, or a bad-input error if it is absent/null/blank -- the
 * required companion to [getOptNonBlankStr]. Use it over [getReqStr] where a whitespace-only value is not a real
 * value (an id, a name), which the plain required accessor would otherwise let through.
 */
fun Map<String, Any?>.getReqNonBlankStr(key: String): String =
    getOptNonBlankStr(key) ?: throw KdrException.mkInput("A non-blank '$key' is required.")

/** The value at [key] coerced to a Long, or a bad-input error if it is absent/null. */
fun Map<String, Any?>.getReqLong(key: String): Long =
    this[key].toOptLong() ?: throw KdrException.mkInput("Missing required field '$key'.")

/** The value at [key] coerced to a Long, or null if it is absent/null/unparseable (the optional [getReqLong]). */
fun Map<String, Any?>.getOptLong(key: String): Long? = this[key].toOptLong()

/** The value at [key] as a `Map<String, Any?>`, or null if it is absent or not a map. */
fun Map<String, Any?>.getOptMap(key: String): Map<String, Any?>? =
    this[key].let { if (it is Map<*, *>) it.toJsonMap() else null }

/** The value at [key] coerced to a Boolean, or null if it is absent/null/unrecognized. */
fun Map<String, Any?>.getOptBool(key: String): Boolean? = when (val v = this[key]) {
    null -> null
    is Boolean -> v
    else -> v.toString().toOptBool()
}

/** Alternative to normal toString() that creates output that is more friendly to humans.
 * This will be expanded later on for dates and objects with specialized interfaces. */
fun Any?.fmt(): String {
    if (this == null) return "null"
    if (this is Double) return fmtD()
    if (this is Float) return fmtF()
    if (this is Instant) return formatDate() // ISO system timestamp, so coerced dates serialize as JSON strings
    if (this is LocalDate) return formatDay() // `yyyy-MM-dd`: a day serializes as the day it is (issue #189)
    return toString()
}

/** We are essentially mandating that code that deals with very small numbers converts to units where the
 * numbers are not so small. Doing this allows us to limit the number of decimal digits we have to deal with. */
fun Double.fmtD(): String = formatFractional(this, 7)

/** We are making a bet that most floating point values actually represent numbers with at most three digits of
 * real precision. It is unusual to use a low-precision representation of a very small floating point number. */
fun Float.fmtF(): String = formatFractional(this.toDouble(), 3)

@KdrPrivate
val pow10 = longArrayOf(1, 10, 100, 1_000, 10_000, 100_000, 1_000_000, 10_000_000)

/**
 * Rounds [value] to at most [maxDigits] fractional digits (ties to even) and trims trailing zeros. The
 * result is always a valid JSON number: the integer part carries no leading zeros, a leading digit
 * introduces any fractional part and holds at least one digit, and there is never a trailing decimal point.
 *
 * Non-finite values (NaN and the infinities) have no JSON number representation, so they render as `null` --
 * consistent with how the parser forgives such values when `strictValues` is off.
 *
 * This is pure Kotlin with no shared mutable state, so it is safe to call concurrently (unlike the
 * `DecimalFormat` it replaced) and is portable to non-JVM targets.
 */
@KdrPrivate
fun formatFractional(value: Double, maxDigits: Int): String {
    if (!value.isFinite()) return "null"
    if (value == 0.0) return "0" // Also folds -0.0 to "0".
    val negative = value < 0.0
    val absV = if (negative) -value else value
    val scale = pow10[maxDigits]
    // Very large magnitudes overflow the Long scaling; fall back to the shortest round-trippable form,
    // which for such values is a valid JSON number (using an `E` exponent when needed).
    if (absV >= (Long.MAX_VALUE / scale).toDouble()) return value.toString()
    val scaled = round(absV * scale).toLong()
    if (scaled == 0L) return "0"
    val sb = StringBuilder()
    if (negative) sb.append('-')
    sb.append(scaled / scale)
    val fracStr = (scaled % scale).toString().padStart(maxDigits, '0').trimEnd('0')
    if (fracStr.isNotEmpty()) sb.append('.').append(fracStr)
    return sb.toString()
}


